import { Component } from '@theme/component';

/**
 * TRB 80s VCR / CRT easter egg — "Canal TRB" (Concept C, channel-surfing).
 *
 * Typing the secret word "TRB" (case-insensitive, judged on the last three
 * keystrokes) summons a CONTAINED retro "CRT TV" overlay centered over a dim,
 * NON-BLOCKING scrim. The set shows one of several "channels"; the user surfs
 * them, and over-tuning PAST the last channel drops into a hidden "phantom"
 * channel that reveals a discount-code reward.
 *
 * State machine:
 *   idle → power-on → surfing(channel N) ⇄ … → (phantom) reward → power-off → idle
 *
 *   - idle:       nothing rendered live except the key-listener watching for "TRB".
 *   - power-on:   a one-shot CSS bloom (bright line → picture) then settles to the
 *                 default channel (CH 02 — MADRID 1985, zero regression).
 *   - surfing:    CH▲/CH▼ (bezel buttons), ↑/↓ or [ ] (keys), and swipe up/down on
 *                 the glass move between channels. Each change plays a ~300ms
 *                 static/snow burst + tracking re-lock, then the channel settles.
 *   - reward:     over-tuning UP past the last channel locks the phantom channel:
 *                 heavy snow → faint signal → the navy hand mascot reveals a code
 *                 (from the `easter_egg_reward_code` theme setting) + a bilingual
 *                 "You found the secret channel." line and a copy-to-clipboard
 *                 affordance. NON-modal — no focus trap.
 *   - power-off:  picture collapses to a line → a bright dot that lingers → fade,
 *                 THEN teardown. Triggered by power button / Esc / click-out / idle.
 *
 * Gated hard:
 *   - The snippet is only rendered when `settings.enable_easter_egg_vhs` is on, so
 *     when the setting is off NOTHING here ships.
 *   - Never blocks commerce: the page underneath keeps scrolling, nothing is
 *     focus-trapped, and dismiss restores full interactivity. The overlay's scrim
 *     is `pointer-events: none` so clicks pass through to the page; only the bezel
 *     buttons + the reward copy button capture pointer events. A click anywhere
 *     outside the TV dismisses.
 *   - Trigger is suppressed while focus is in an input/textarea/select/contenteditable
 *     so it can never fire mid-typing, and surf keys are likewise ignored while
 *     editing (though once open the egg owns ↑/↓/Esc/etc.).
 *   - Reduced motion (live `matchMedia`, mirroring trb-hand-cursor.js): NO
 *     snow/jitter/bloom/power animation. Channels HARD-CUT, power on/off is
 *     instant, the reward still reveals (and is still reachable by surfing) — just
 *     without animation. The CSS also gates the look; this flag gates JS timing so
 *     transitions are zero-delay.
 *
 * Performance: one overlay element, CSS-driven artefacts (no per-frame JS for the
 * look). A single rAF drives the running timecode and only runs while open + the
 * tab is visible + the window is focused; it is cancelled on close. The
 * key-listener is the only thing live while the egg is dormant; everything else is
 * created on trigger and torn down on dismiss (every listener/timer added on open
 * is removed on close).
 *
 * @extends Component
 */
class TrbVhs extends Component {
  /** Auto-dismiss after this many ms of no interaction. */
  static AUTO_DISMISS_MS = 30000;

  /** Channel-change static/snow burst duration (ms). Mirrors the CSS .35s window. */
  static SNOW_MS = 320;

  /** Power-off collapse-to-dot duration before teardown (ms). */
  static POWER_OFF_MS = 360;

  /** Power-on bloom duration (ms) — informational; CSS owns the animation. */
  static POWER_ON_MS = 420;

  /** The secret sequence, lowercased. */
  static SECRET = 'trb';

  /** @type {boolean} Whether the CRT overlay is currently open. */
  #open = false;

  /** @type {string} Rolling buffer of the last few keystrokes (lowercased). */
  #buffer = '';

  /**
   * @type {'idle'|'power-on'|'surfing'|'reward'|'power-off'} Current machine state.
   * Tracked so transitions never overlap (e.g. ignore surf input mid-power-off).
   */
  #state = 'idle';

  /** @type {number} Index of the currently-shown channel (into #channels). */
  #channel = 0;

  /** @type {Array<{id:string, name_en:string, name_es:string}>} Channel list. */
  #channels = [];

  /** @type {boolean} Whether the phantom reward is currently revealed. */
  #rewardShown = false;

  /** @type {number | null} Pending requestAnimationFrame handle for the timecode. */
  #frame = null;

  /** @type {number} Performance-clock origin used to derive the running timecode. */
  #clockStart = 0;

  /** @type {number} Last whole-second value painted (so we only write on change). */
  #lastSecond = -1;

  /** @type {number | null} The auto-dismiss timeout handle. */
  #autoDismiss = null;

  /** @type {number | null} Pending channel-settle timeout (after a snow burst). */
  #settleTimer = null;

  /** @type {number | null} Pending power-off teardown timeout. */
  #powerOffTimer = null;

  /** @type {MediaQueryList | null} Live reduced-motion gate (mirrors hand-cursor). */
  #reduceMotion = null;

  /** @type {HTMLElement | null} The timecode readout element (updated each tick). */
  #timecodeEl = null;

  /** @type {HTMLElement | null} The flashing "CH NN" indicator (top-right). */
  #chBadgeEl = null;

  /** @type {HTMLElement | null} The aria-live region for announcements. */
  #liveEl = null;

  /** @type {Element | null} The element focused before we opened, to restore later. */
  #returnFocus = null;

  /** @type {number | null} Touch-swipe start Y on the glass; null when not tracking. */
  #touchStartY = null;

  connectedCallback() {
    super.connectedCallback();

    // Read channel metadata + the reward code from the data-attributes the liquid
    // wrote (the code is NEVER hardcoded here — it comes from the theme setting).
    this.#channels = this.#readChannels();

    // Live reduced-motion gate, same approach as trb-hand-cursor.js — re-evaluated
    // on the fly so toggling the OS setting flips animation off without a reload.
    this.#reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // The only always-on listener: watch for the secret word. Passive, cheap.
    document.addEventListener('keydown', this.#handleKeydown, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#handleKeydown);
    this.#teardown();
  }

  /** @returns {boolean} Whether motion should be suppressed right now. */
  #prefersReducedMotion() {
    return Boolean(this.#reduceMotion && this.#reduceMotion.matches);
  }

  /**
   * Parse the channel list the liquid serialised onto `[data-trb-vhs-channels]`.
   * Falls back to a single default channel so the egg always opens.
   * @returns {Array<{id:string, name_en:string, name_es:string}>}
   */
  #readChannels() {
    const raw = this.dataset.trbVhsChannels;
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      } catch (_) {
        /* fall through to default */
      }
    }
    return [{ id: 'ident', name_en: 'CANAL TRB', name_es: 'CANAL TRB' }];
  }

  /**
   * Watch keystrokes for the secret word, and (while open) drive surf/close keys.
   * Ignored entirely when focus is in a text-entry field so it never fires
   * mid-typing.
   * @type {(event: KeyboardEvent) => void}
   */
  #handleKeydown = (event) => {
    // While open, the egg owns a small set of keys. Never act while typing in a
    // field (reuse the editing-target guard) so forms keep working.
    if (this.#open) {
      if (this.#isEditingTarget(event.target) || this.#isEditingTarget(document.activeElement)) {
        return;
      }
      switch (event.key) {
        case 'Escape':
          this.#powerOff();
          break;
        case 'ArrowUp':
        case ']':
          this.#surf(1);
          break;
        case 'ArrowDown':
        case '[':
          this.#surf(-1);
          break;
        default:
          return;
      }
      return;
    }

    // Dormant: never sniff keys while the user is typing into a field.
    if (this.#isEditingTarget(event.target) || this.#isEditingTarget(document.activeElement)) {
      return;
    }

    // Only single printable characters extend the buffer; modifiers/arrows are
    // ignored. Keep the buffer to the secret's length.
    if (event.key.length !== 1) return;

    this.#buffer = (this.#buffer + event.key.toLowerCase()).slice(-TrbVhs.SECRET.length);
    if (this.#buffer === TrbVhs.SECRET) {
      this.#buffer = '';
      this.#openOverlay();
    }
  };

  /**
   * Is the event/active target a text-entry surface we must not sniff?
   * @param {EventTarget | null} node
   * @returns {boolean}
   */
  #isEditingTarget(node) {
    if (!(node instanceof Element)) return false;
    if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement || node instanceof HTMLSelectElement) {
      return true;
    }
    return node.isContentEditable || Boolean(node.closest('[contenteditable=""], [contenteditable="true"]'));
  }

  /** Build + show the CRT overlay and wire its affordances. Idempotent. */
  #openOverlay() {
    if (this.#open) return;
    this.#open = true;
    this.#returnFocus = document.activeElement;

    // Cache the live nodes once per open.
    this.#timecodeEl = this.querySelector('[data-trb-vhs-timecode]');
    this.#chBadgeEl = this.querySelector('[data-trb-vhs-ch-badge]');
    this.#liveEl = this.querySelector('[data-trb-vhs-live]');

    // Reveal the markup that ships inert in the snippet (display:none until open).
    this.dataset.open = 'true';
    this.setAttribute('aria-hidden', 'false');

    // Default channel = CH 02 (MADRID 1985 title card) — exact zero-regression open.
    const defaultIndex = this.#channels.findIndex((c) => c.id === 'titlecard');
    this.#channel = defaultIndex >= 0 ? defaultIndex : 0;
    this.#rewardShown = false;
    this.dataset.reward = 'false';
    this.#showChannel(this.#channel, { announce: false, snow: false });

    // Power-ON transition. CSS owns the bloom; JS just flags the state so the look
    // applies, then settles to "surfing". Under reduced motion the flag still flips
    // but the CSS animation is suppressed, so it reads as an instant cut.
    this.#state = 'power-on';
    this.dataset.state = 'power-on';
    if (this.#prefersReducedMotion()) {
      this.#enterSurfing();
    } else {
      this.#powerOffTimer = window.setTimeout(this.#enterSurfing, TrbVhs.POWER_ON_MS);
    }

    // Running timecode via a single rAF loop — only while open + visible + focused.
    this.#clockStart = performance.now();
    this.#lastSecond = -1;
    this.#startClock();

    // Auto-dismiss so the egg never lingers / never traps the user.
    this.#armAutoDismiss();

    // Affordances (added on open, removed on close).
    this.querySelector('[data-trb-vhs-power]')?.addEventListener('click', this.#handlePowerClick);
    this.querySelector('[data-trb-vhs-ch-up]')?.addEventListener('click', this.#handleChUp);
    this.querySelector('[data-trb-vhs-ch-down]')?.addEventListener('click', this.#handleChDown);
    this.querySelector('[data-trb-vhs-copy]')?.addEventListener('click', this.#handleCopy);

    const scrim = this.querySelector('[data-trb-vhs-scrim]');
    scrim?.addEventListener('click', this.#handlePowerClick);
    // Click-outside: the scrim ignores pointer events (so the page stays usable),
    // so we also catch clicks anywhere outside the TV via a capturing listener.
    document.addEventListener('pointerdown', this.#handleOutsidePointer, true);

    // Touch swipe on the glass = surf.
    const glass = this.querySelector('[data-trb-vhs-glass]');
    glass?.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    glass?.addEventListener('touchend', this.#handleTouchEnd, { passive: true });

    // Pause/resume the timecode clock with tab visibility + window focus.
    document.addEventListener('visibilitychange', this.#handleVisibility);
    window.addEventListener('blur', this.#stopClock);
    window.addEventListener('focus', this.#startClock);

    // Move keyboard focus to the power button so Enter/Space can dismiss, but do
    // NOT trap focus — Tab still walks the page underneath.
    this.querySelector('[data-trb-vhs-power]')?.focus({ preventScroll: true });
  }

  /** Leave the power-on bloom and become interactive. */
  #enterSurfing = () => {
    if (this.#powerOffTimer !== null) {
      clearTimeout(this.#powerOffTimer);
      this.#powerOffTimer = null;
    }
    if (!this.#open) return;
    this.#state = 'surfing';
    this.dataset.state = 'surfing';
    this.#announceChannel();
  };

  /**
   * Surf by `dir` (+1 = CH up / over-tune, -1 = CH down). Over-tuning UP past the
   * last channel locks the phantom reward; tuning DOWN from the reward returns to
   * the last channel. Any surf resets the idle timer.
   * @param {1 | -1} dir
   */
  #surf(dir) {
    // Only surf once the picture has settled and we're not mid-power-off.
    if (this.#state !== 'surfing' && this.#state !== 'reward') return;
    this.#armAutoDismiss();

    // From the reward, UP holds (already at the end); DOWN drops back to channels.
    if (this.#rewardShown) {
      if (dir < 0) this.#hideRewardToChannel(this.#channels.length - 1);
      return;
    }

    const next = this.#channel + dir;

    // Over-tune past the last channel → phantom reward.
    if (next >= this.#channels.length) {
      this.#revealReward();
      return;
    }
    // Tuning below the first channel wraps to the last (gives the dial a full loop).
    const target = next < 0 ? this.#channels.length - 1 : next;
    if (target === this.#channel) return;

    this.#channel = target;
    this.#showChannel(target, { announce: true, snow: true });
  }

  /**
   * Switch the visible channel. Plays a snow burst + tracking re-lock unless
   * suppressed (initial open) or reduced-motion is on (hard cut).
   * @param {number} index
   * @param {{announce:boolean, snow:boolean}} opts
   */
  #showChannel(index, { announce, snow }) {
    const channel = this.#channels[index];
    if (!channel) return;

    // Flash the channel number top-right.
    if (this.#chBadgeEl) {
      this.#chBadgeEl.textContent = this.#chLabel(index);
      // Retrigger the flash animation by toggling the data flag off→on next frame.
      this.#chBadgeEl.dataset.flash = 'false';
      requestAnimationFrame(() => {
        if (this.#chBadgeEl) this.#chBadgeEl.dataset.flash = 'true';
      });
    }

    const apply = () => {
      this.dataset.channel = String(index);
      this.dataset.channelId = channel.id;
      if (announce) this.#announceChannel();
    };

    if (this.#settleTimer !== null) {
      clearTimeout(this.#settleTimer);
      this.#settleTimer = null;
    }

    if (snow && !this.#prefersReducedMotion()) {
      // Static/snow burst, then settle into the new channel + tracking re-lock.
      this.dataset.snow = 'true';
      this.#settleTimer = window.setTimeout(() => {
        this.dataset.snow = 'false';
        this.#settleTimer = null;
        apply();
      }, TrbVhs.SNOW_MS);
    } else {
      // Hard cut (initial render or reduced motion).
      this.dataset.snow = 'false';
      apply();
    }
  }

  /** Reveal the hidden phantom channel + the reward (code + bilingual line). */
  #revealReward() {
    if (this.#rewardShown) return;
    this.#armAutoDismiss();
    this.#rewardShown = true;
    this.#state = 'reward';
    this.dataset.state = 'reward';

    if (this.#chBadgeEl) {
      this.#chBadgeEl.textContent = '——';
      this.#chBadgeEl.dataset.flash = 'false';
      requestAnimationFrame(() => {
        if (this.#chBadgeEl) this.#chBadgeEl.dataset.flash = 'true';
      });
    }

    const apply = () => {
      this.dataset.reward = 'true';
      this.dataset.channelId = 'phantom';
      this.#announce(this.#liveEl?.dataset.rewardMsg || 'Secret channel found.');
    };

    if (this.#settleTimer !== null) {
      clearTimeout(this.#settleTimer);
      this.#settleTimer = null;
    }

    if (this.#prefersReducedMotion()) {
      this.dataset.snow = 'false';
      apply();
    } else {
      // Heavy snow → faint signal locks → reward settles. Reuses the snow burst,
      // held a touch longer for the "phantom" feel.
      this.dataset.snow = 'true';
      this.#settleTimer = window.setTimeout(() => {
        this.dataset.snow = 'false';
        this.#settleTimer = null;
        apply();
      }, TrbVhs.SNOW_MS + 120);
    }
  }

  /**
   * Leave the reward and return to a real channel (tuning down from the phantom).
   * @param {number} index
   */
  #hideRewardToChannel(index) {
    this.#rewardShown = false;
    this.dataset.reward = 'false';
    this.#state = 'surfing';
    this.dataset.state = 'surfing';
    this.#channel = Math.max(0, Math.min(index, this.#channels.length - 1));
    this.#showChannel(this.#channel, { announce: true, snow: true });
  }

  /** @param {number} index @returns {string} e.g. "CH 02" / phantom dashes. */
  #chLabel(index) {
    const n = String(index + 1).padStart(2, '0');
    return `CH ${n}`;
  }

  /** Announce the current channel to the aria-live region. */
  #announceChannel() {
    const channel = this.#channels[this.#channel];
    if (!channel || !this.#liveEl) return;
    const lang = this.#liveEl.dataset.lang === 'es' ? 'es' : 'en';
    const name = lang === 'es' ? channel.name_es : channel.name_en;
    const chWord = this.#liveEl.dataset.chWord || 'Channel';
    this.#announce(`${chWord} ${this.#chLabel(this.#channel)}: ${name}`);
  }

  /** @param {string} message Write to the polite live region. */
  #announce(message) {
    if (this.#liveEl) this.#liveEl.textContent = message;
  }

  /** @type {(event: MouseEvent) => void} */
  #handlePowerClick = () => this.#powerOff();

  /** @type {(event: MouseEvent) => void} */
  #handleChUp = () => this.#surf(1);

  /** @type {(event: MouseEvent) => void} */
  #handleChDown = () => this.#surf(-1);

  /**
   * Copy the reward code to the clipboard and confirm bilingually.
   * @type {(event: MouseEvent) => void}
   */
  #handleCopy = async (event) => {
    this.#armAutoDismiss();
    const btn = event.currentTarget;
    if (!(btn instanceof HTMLElement)) return;
    const code = this.dataset.trbVhsReward || '';
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      btn.dataset.copied = 'true';
      this.#announce(btn.dataset.copiedMsg || 'Copied');
      window.setTimeout(() => {
        btn.dataset.copied = 'false';
      }, 1600);
    } catch (_) {
      // Clipboard blocked (permissions / insecure context). Silently no-op — the
      // code is plainly visible on screen for the user to copy by hand.
    }
  };

  /**
   * A pointerdown outside the TV set closes the egg. The TV (and its bezel
   * controls) live inside `[data-trb-vhs-tv]`, so anything else dismisses.
   * @type {(event: PointerEvent) => void}
   */
  #handleOutsidePointer = (event) => {
    const tv = this.querySelector('[data-trb-vhs-tv]');
    if (tv && event.target instanceof Node && !tv.contains(event.target)) {
      this.#powerOff();
    }
  };

  /** @type {(event: TouchEvent) => void} Record swipe start. */
  #handleTouchStart = (event) => {
    this.#touchStartY = event.changedTouches[0]?.clientY ?? null;
  };

  /** @type {(event: TouchEvent) => void} A vertical swipe surfs. */
  #handleTouchEnd = (event) => {
    if (this.#touchStartY === null) return;
    const endY = event.changedTouches[0]?.clientY ?? this.#touchStartY;
    const dy = endY - this.#touchStartY;
    this.#touchStartY = null;
    if (Math.abs(dy) < 36) return; // ignore taps / tiny drags
    // Swipe UP (dy negative) = CH up / over-tune; swipe DOWN = CH down.
    this.#surf(dy < 0 ? 1 : -1);
  };

  /** (Re)arm the inactivity auto-dismiss. */
  #armAutoDismiss() {
    if (this.#autoDismiss !== null) clearTimeout(this.#autoDismiss);
    this.#autoDismiss = window.setTimeout(this.#powerOff, TrbVhs.AUTO_DISMISS_MS);
  }

  /** @type {() => void} Pause the clock when the tab is hidden, resume when shown. */
  #handleVisibility = () => {
    if (document.hidden) {
      this.#stopClock();
    } else {
      this.#startClock();
    }
  };

  /** Start the rAF timecode loop if it isn't already running and we're visible. */
  #startClock = () => {
    if (!this.#open || this.#frame !== null) return;
    if (document.hidden) return;
    // Rebase so the timecode doesn't jump after a pause.
    this.#clockStart = performance.now() - this.#lastSecond * 1000;
    this.#frame = requestAnimationFrame(this.#tick);
  };

  /** Stop the rAF timecode loop. */
  #stopClock = () => {
    if (this.#frame !== null) {
      cancelAnimationFrame(this.#frame);
      this.#frame = null;
    }
  };

  /**
   * rAF tick: repaint the timecode only when the whole-second value changes, then
   * re-schedule. Runs only while open + visible + focused.
   * @type {() => void}
   */
  #tick = () => {
    this.#frame = null;
    if (!this.#open) return;

    const seconds = Math.floor((performance.now() - this.#clockStart) / 1000);
    if (seconds !== this.#lastSecond) {
      this.#lastSecond = seconds;
      this.#renderTimecode(seconds);
    }
    this.#frame = requestAnimationFrame(this.#tick);
  };

  /**
   * Format elapsed seconds as HH:MM:SS and write it to the readout.
   * @param {number} total
   */
  #renderTimecode(total) {
    if (!this.#timecodeEl) return;
    const hh = String(Math.floor(total / 3600)).padStart(2, '0');
    const mm = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
    const ss = String(total % 60).padStart(2, '0');
    this.#timecodeEl.textContent = `${hh}:${mm}:${ss}`;
  }

  /**
   * Begin the power-OFF transition (collapse to a line → dot → fade), THEN tear
   * down. Under reduced motion this is instant. Idempotent / safe to call from any
   * dismiss path (power button, Esc, click-out, idle).
   */
  #powerOff = () => {
    if (!this.#open || this.#state === 'power-off') return;
    this.#state = 'power-off';
    this.dataset.state = 'power-off';

    // Stop the running clock + idle timer immediately; the picture is going away.
    this.#stopClock();
    if (this.#autoDismiss !== null) {
      clearTimeout(this.#autoDismiss);
      this.#autoDismiss = null;
    }
    if (this.#settleTimer !== null) {
      clearTimeout(this.#settleTimer);
      this.#settleTimer = null;
    }
    if (this.#powerOffTimer !== null) {
      clearTimeout(this.#powerOffTimer);
      this.#powerOffTimer = null;
    }

    if (this.#prefersReducedMotion()) {
      this.#teardown();
    } else {
      this.#powerOffTimer = window.setTimeout(this.#teardown, TrbVhs.POWER_OFF_MS);
    }
  };

  /** Hide the overlay and remove EVERY timer + listener added while open. */
  #teardown = () => {
    if (this.#powerOffTimer !== null) {
      clearTimeout(this.#powerOffTimer);
      this.#powerOffTimer = null;
    }
    if (!this.#open) {
      this.#state = 'idle';
      this.dataset.state = 'idle';
      return;
    }
    this.#open = false;
    this.#state = 'idle';

    this.dataset.open = 'false';
    this.dataset.state = 'idle';
    this.dataset.reward = 'false';
    this.dataset.snow = 'false';
    this.setAttribute('aria-hidden', 'true');

    this.#stopClock();
    if (this.#autoDismiss !== null) {
      clearTimeout(this.#autoDismiss);
      this.#autoDismiss = null;
    }
    if (this.#settleTimer !== null) {
      clearTimeout(this.#settleTimer);
      this.#settleTimer = null;
    }

    this.querySelector('[data-trb-vhs-power]')?.removeEventListener('click', this.#handlePowerClick);
    this.querySelector('[data-trb-vhs-ch-up]')?.removeEventListener('click', this.#handleChUp);
    this.querySelector('[data-trb-vhs-ch-down]')?.removeEventListener('click', this.#handleChDown);
    this.querySelector('[data-trb-vhs-copy]')?.removeEventListener('click', this.#handleCopy);
    this.querySelector('[data-trb-vhs-scrim]')?.removeEventListener('click', this.#handlePowerClick);
    document.removeEventListener('pointerdown', this.#handleOutsidePointer, true);

    const glass = this.querySelector('[data-trb-vhs-glass]');
    glass?.removeEventListener('touchstart', this.#handleTouchStart);
    glass?.removeEventListener('touchend', this.#handleTouchEnd);

    document.removeEventListener('visibilitychange', this.#handleVisibility);
    window.removeEventListener('blur', this.#stopClock);
    window.removeEventListener('focus', this.#startClock);

    // Clear the live region before dropping the reference so a stale announcement
    // isn't read out on a later reopen.
    if (this.#liveEl) this.#liveEl.textContent = '';

    this.#timecodeEl = null;
    this.#chBadgeEl = null;
    this.#liveEl = null;
    this.#buffer = '';
    this.#rewardShown = false;
    this.#touchStartY = null;

    // Restore focus to wherever it was, without yanking the scroll position.
    if (this.#returnFocus instanceof HTMLElement) {
      this.#returnFocus.focus({ preventScroll: true });
    }
    this.#returnFocus = null;
  };
}

if (!customElements.get('trb-vhs')) {
  customElements.define('trb-vhs', TrbVhs);
}
